import React, { useState, useEffect } from "react";
import { useLayout } from "../../Layouts/LayoutContext";
import EnclosureSelectorEnhanced from "../../PanelLayout/Enclosure/EnclosureSelectorEnhanced";
import ScenarioSelector from "../../PanelLayout/Scenarios/ScenarioSelector";
import PlaceholderDuctHint from "../../Common/PlaceholderDuctHint";
import { usePlaceholderDucts } from "../../../hooks/usePlaceholderDucts";
import "../../Layouts/Layout.css";

const TABS = [["enclosure", "Enclosure"], ["scenario", "Scenario"], ["climatization", "Climatization"]];
const TAB_IDS = TABS.map(([id]) => id);

export default function SetupPanel({ initialTab = "enclosure", hideTabs = false }) {
  const L = useLayout();
  const startTab = TAB_IDS.includes(initialTab) ? initialTab : "enclosure";
  const [tab, setTab] = useState(startTab);
  const r = L.climateResult;
  const climateWarn = !!r && r.temperature > L.climate.maxInner;

  // Ducts of the selected scenario that fall back to placeholders
  const placeholderDucts = usePlaceholderDucts(L.selectedScenario?.settings);
  const ductWarn = L.hasEnclosure && placeholderDucts.any;

  // The iframe got a new ?tab=... without remounting the panel
  useEffect(() => {
    setTab(startTab);
  }, [startTab]);

  return (
    <div className="pl-panel">
      {!hideTabs && (
        <div className="pl-tabs" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id}
              className={`pl-tab ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>
              {label}
              {id === "scenario" && ductWarn && <span className="pl-badge" aria-label="Placeholder ducts used">!</span>}
              {id === "climatization" && climateWarn && <span className="pl-badge" aria-label="Climatization required">!</span>}
            </button>
          ))}
        </div>
      )}

      <div className="pl-body">
        {tab === "enclosure" && L.selectedProject && (
          <EnclosureSelectorEnhanced
            selectedProject={L.selectedProject}
            onUpdateProject={L.updateProject}
            requiredHeight={L.requiredHeight}
            enclosureTooSmall={L.enclosureTooSmall}
            onClearEnclosureBlock={L.clearEnclosureBlock}
            onEnclosureChanged={L.onEnclosureChanged}
          />
        )}

        {tab === "scenario" && (L.hasEnclosure
          ? <>
              {L.selectedScenario && (
                <PlaceholderDuctHint
                  enclosure={placeholderDucts.enclosure}
                  sections={placeholderDucts.sections}
                >
                  Select real ducts in the scenario (“Create or edit scenario” → Panel Settings) before exporting.
                </PlaceholderDuctHint>
              )}
              <ScenarioSelector
                selectedProject={L.selectedProject}
                selectedScenario={L.selectedScenario}
                onSelectScenario={L.handleScenarioSelected}
                onCalculateLayout={L.calculateLayout}
                isCalculating={L.isCalculating}
              />
            </>
          : <p className="pl-hint">Choose an enclosure first.</p>)}

        {tab === "climatization" && <Climatization warn={climateWarn} />}
      </div>
    </div>
  );
}

function Climatization({ warn }) {
  const { climate, setClimate, climateResult: r } = useLayout();
  const set = (k) => (e) => setClimate({ ...climate, [k]: Number(e.target.value) });
  const num = (k, label) => (
    <label className="pl-field"><input className="pl-input" type="number" value={climate[k]} onChange={set(k)} /><small>{label}</small></label>
  );
  const sel = (k, opts, label, unit = "") => (
    <label className="pl-field">
      <select className="pl-input" value={climate[k]} onChange={set(k)}>{opts.map((o) => <option key={o} value={o}>{o}{unit}</option>)}</select>
      <small>{label}</small>
    </label>
  );
  return (
    <>
      {warn && (
        <div className="pl-alert" role="alert">
          <strong>Climatization required</strong>
          <p>Temperature without climatization: {r.temperature}°C</p>
          <p>Max. accepted inner temperature: {climate.maxInner}°C</p>
        </div>
      )}
      <h3>Calculated power loss</h3>
      <div className="pl-big">{r ? `${r.powerLoss} W` : "–"}</div>
      <h3>Define calculation conditions</h3>
      {sel("altitude", [0, 50, 500, 1000, 2000], "Height above sea level", " m")}
      <div className="pl-row2">{num("minInner", "Min. inner temp °C")}{num("maxInner", "Max. inner temp °C")}</div>
      <div className="pl-row2">{num("minOuter", "Min. outer temp °C")}{num("maxOuter", "Max. outer temp °C")}</div>
      {sel("voltage", [110, 230, 400], "Voltage")}
      {sel("frequency", [50, 60], "Frequency")}
    </>
  );
}
